import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import {
  FaHome,
  FaUser,
  FaQuestionCircle,
  FaChevronDown,
  FaChevronUp,
  FaEnvelope,
  FaPhone,
  FaVideo,
  FaCog,
  FaSearch,
  FaCalendarAlt,
  FaCreditCard,
  FaComments,
  FaHeart,
  FaMapMarkerAlt,
  FaUsers,
  FaBook,
  FaClock,
  FaPlus,
  FaList
} from 'react-icons/fa';
import './HelpPage.css';

const SECTIONS = [
  {
    id: 'stays',
    title: 'Stays & Accommodation',
    icon: FaHome,
    subtitle: 'Book hotels, villas, and vacation rentals',
    features: [
      { title: 'Search & Browse', desc: 'Search by destination, dates, and guests. Filter by price, property type (Hotel, Villa, Resort), and amenities. View photos, reviews, and map location.', path: '/search', icon: FaSearch },
      { title: 'Property Details', desc: 'Full info on amenities, house rules, policies, and Q&A. Ask hosts questions. Read reviews from past guests.', path: '/properties', icon: FaMapMarkerAlt },
      { title: 'Booking & Payment', desc: 'Select dates and guests → Review details → Secure payment. Hosts approve requests; you receive confirmation.', path: '/booking', icon: FaCreditCard },
      { title: 'Trips', desc: 'View all bookings (pending, confirmed, cancelled). Message hosts, download receipts, or cancel with a reason. Filter by status.', path: '/trips', icon: FaCalendarAlt },
      { title: 'Messaging', desc: 'Chat with hosts from Trips or Booking Details. Send messages, see read receipts. Hosts reply from their dashboard.', path: '/trips', icon: FaComments },
      { title: 'Wishlist', desc: 'Save properties for later. Access from Account or the heart icon in the navbar.', path: '/wishlist', icon: FaHeart }
    ]
  },
  {
    id: 'host',
    title: 'Host Dashboard',
    icon: FaUser,
    subtitle: 'Manage your properties and guests',
    features: [
      { title: 'Dashboard', desc: 'Overview of enquiries, messages, and properties. Quick access to pending actions.', path: '/host', icon: FaList },
      { title: 'Enquiries', desc: 'Review booking requests. Approve or decline. Filter by status (pending, approved, declined). Bulk actions available.', path: '/host', icon: FaCalendarAlt },
      { title: 'Guest Messages', desc: 'Receive and reply to guest messages. Chat-style interface. Mark as read. Full conversation history.', path: '/host', icon: FaComments },
      { title: 'Properties', desc: 'Add, edit, and manage listings. Set pricing, amenities, policies, photos. Activate or deactivate.', path: '/host', icon: FaHome }
    ]
  },
  {
    id: 'meetings',
    title: 'Meeting Rooms',
    icon: FaVideo,
    subtitle: 'Book meeting spaces and manage teams',
    features: [
      { title: 'Meeting Home', desc: 'Landing page for meeting bookings. Search available rooms by location, capacity, and amenities.', path: '/meeting-home', icon: FaHome },
      { title: 'Browse Rooms', desc: 'View all meeting spaces. Filter by capacity, amenities (WiFi, projector, AC). See availability.', path: '/meetings/rooms', icon: FaSearch },
      { title: 'Book a Room', desc: 'Select date, time slot, and room. Add purpose and attendee count. Confirm booking.', path: '/meetings/book', icon: FaBook },
      { title: 'Schedule', desc: 'Calendar view of your meeting bookings. See time slots, room assignments, and team meetings.', path: '/meetings/schedule', icon: FaCalendarAlt },
      { title: 'My Bookings', desc: 'View upcoming and past meeting bookings. Manage reservations.', path: '/meetings/my-bookings', icon: FaList },
      { title: 'Search Results', desc: 'Search meeting rooms and view filtered results by location or criteria.', path: '/meetings/search', icon: FaSearch },
      { title: 'Add Your Space', desc: 'List your meeting room or office space. Add room name, amenities, and availability.', path: '/meetings/add-your-space', icon: FaPlus },
      { title: 'Create Team', desc: 'Create teams, add members, and manage team bookings.', path: '/meetings/create-team', icon: FaUsers },
      { title: 'Favourites', desc: 'Save favourite meeting rooms for quick access.', path: '/meetings/favourite', icon: FaHeart },
      { title: 'Profile', desc: 'Manage your meeting profile and preferences.', path: '/meetings/profile', icon: FaUser },
      { title: 'Dashboard', desc: 'Room & Member Status Dashboard: live view of room availability and team member status (in meeting / free).', path: '/meetings/status', icon: FaClock }
    ]
  },
  {
    id: 'account',
    title: 'Account & Support',
    icon: FaCog,
    subtitle: 'Settings and help resources',
    features: [
      { title: 'Account Settings', desc: 'Profile overview, personal info, login & security. Payments, preferences, wishlist, reviews, privacy.', path: '/account', icon: FaCog },
      { title: 'Notifications', desc: 'Booking confirmations, messages, host replies. Manage notification preferences.', path: '/notifications', icon: FaEnvelope },
      { title: 'Contact Support', desc: 'Phone or email from Booking Details. Email host for property questions.', path: null, icon: FaPhone }
    ]
  }
];

const FAQ_ITEMS = [
  { q: 'How do I cancel a stay booking?', a: 'Go to Trips → select your booking → Cancel Booking. Choose a reason from the dropdown. Refunds follow the property\'s cancellation policy.' },
  { q: 'How does guest-host messaging work?', a: 'Guests message from Trips or Booking Details. Hosts see messages in their dashboard and reply. Both see the full conversation with read receipts.' },
  { q: 'When is my stay booking confirmed?', a: 'After payment, the host receives the request. Once they approve, your booking is confirmed and you\'ll get a notification.' },
  { q: 'How do I become a host?', a: 'Complete host onboarding from your account. Add property details, photos, and pricing. Listings go live after approval.' },
  { q: 'How do I book a meeting room?', a: 'Go to Meeting Home or Meetings → Rooms. Search by location/capacity, select date and time slot, add purpose, and confirm.' },
  { q: 'How do I add my meeting space?', a: 'Go to Meetings → Add Your Space. Add room name, amenities (WiFi, projector, etc.), and availability. Requires login.' },
  { q: 'What payment methods are accepted?', a: 'We accept major credit/debit cards and other payment options at checkout. Payment is processed securely.' }
];

const HelpPage = () => {
  const [expandedFaq, setExpandedFaq] = useState(null);

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="help-page">
      <Navbar />
      <div className="help-page-wrapper">
        <aside className="help-sidebar">
          <nav className="help-nav">
            <h3>Quick Links</h3>
            {SECTIONS.map((s) => (
              <button
                key={s.id}
                className="nav-item"
                onClick={() => scrollToSection(s.id)}
              >
                <s.icon className="nav-icon" />
                {s.title}
              </button>
            ))}
            <button className="nav-item" onClick={() => scrollToSection('faq')}>
              <FaQuestionCircle className="nav-icon" />
              FAQ
            </button>
            <button className="nav-item" onClick={() => scrollToSection('contact')}>
              <FaPhone className="nav-icon" />
              Contact
            </button>
          </nav>
        </aside>

        <main className="help-main">
          <header className="help-header">
            <div className="help-header-content">
              <FaQuestionCircle className="help-header-icon" />
              <div>
                <h1>Help & Support</h1>
                <p>Everything you need to use Book & Stay — stays, meetings, and more</p>
              </div>
            </div>
          </header>

          {SECTIONS.map((section) => (
            <section key={section.id} id={section.id} className="help-section">
              <h2>
                <section.icon className="section-icon" />
                {section.title}
              </h2>
              <p className="section-subtitle">{section.subtitle}</p>
              <div className="feature-grid">
                {section.features.map((f, i) => (
                  <div key={i} className="feature-card">
                    <div className="feature-icon">
                      <f.icon />
                    </div>
                    <h4>{f.title}</h4>
                    <p>{f.desc}</p>
                    {f.path && (
                      <Link to={f.path} className="feature-link">
                        Open →
                      </Link>
                    )}
                  </div>
                ))}
              </div>
            </section>
          ))}

          <section id="faq" className="help-section help-faq-section">
            <h2>
              <FaQuestionCircle className="section-icon" />
              Frequently Asked Questions
            </h2>
            <div className="faq-list">
              {FAQ_ITEMS.map((item, i) => (
                <div
                  key={i}
                  className={`faq-item ${expandedFaq === i ? 'expanded' : ''}`}
                  onClick={() => setExpandedFaq(expandedFaq === i ? null : i)}
                >
                  <div className="faq-question">
                    <span>{item.q}</span>
                    {expandedFaq === i ? <FaChevronUp /> : <FaChevronDown />}
                  </div>
                  {expandedFaq === i && <div className="faq-answer">{item.a}</div>}
                </div>
              ))}
            </div>
          </section>

          <section id="contact" className="help-section help-contact-section">
            <h2>
              <FaPhone className="section-icon" />
              Contact Us
            </h2>
            <p className="contact-intro">Need help? Reach out anytime.</p>
            <div className="contact-cards">
              <a href="tel:+9118001234567" className="contact-card">
                <FaPhone className="contact-card-icon" />
                <span className="contact-label">Phone</span>
                <span className="contact-value">+91 1800-123-4567</span>
              </a>
              <a href="mailto:support@bookandstay.com" className="contact-card">
                <FaEnvelope className="contact-card-icon" />
                <span className="contact-label">Email</span>
                <span className="contact-value">support@bookandstay.com</span>
              </a>
            </div>
          </section>
        </main>
      </div>
      <Footer />
    </div>
  );
};

export default HelpPage;
